import * as React from 'react'
import { observer } from 'mobx-react-lite'
import { useInjection } from '@core/providers/inversify-react.provider'
import { AppPresenter } from '@app/app.presenter'
import { ShellComponent } from '@core/infra/shell/shell.component'
import { RouteProgressBarComponent } from './route-progress-bar.component'
import { TopRightStackComponent } from './top-right-stack.component'
import { DialogComponent } from '@core/infra/dialog/dialog.component'
import { BannerStackComponent } from '@core/infra/connectivity/banner-stack.component'
import type { RouteId } from '@core/routing/router.config'
import type { PageEntry } from '@app/routes'
import { PageSlotComponent } from './page-slot.component'

type Props = {
  routeComponents: Record<RouteId, PageEntry>
}

/**
 * `import.meta.env.DEV &&` alone does not keep this out of the production
 * bundle: the import is still statically reachable, so Rollup can't prove it
 * dead and ships the whole module (verified by grepping dist/ — the panel's
 * copy showed up in a plain production build). Wrapping the dynamic
 * `import()` itself in a build-time-constant ternary that folds to `null` in
 * production lets Rollup drop the chunk entirely, the same guarantee
 * `enterAsGuest`'s `import.meta.env.DEV` guard gives a Repository method.
 */
const DebugOperationsComponent = import.meta.env.DEV
  ? React.lazy(() =>
      import('@core/infra/debug-operations/debug-operations.component').then((module) => ({
        default: module.DebugOperationsComponent,
      })),
    )
  : null

export const AppLayoutComponent = observer((props: Props) => {
  const { routeComponents } = props
  const presenter: AppPresenter = useInjection(AppPresenter)
  const { routeId, routeDef } = presenter.currentRoute

  if (!routeId) {
    return <div className="container">{presenter.loadingLabel}</div>
  }

  const entry = routeComponents[routeId]
  const page = <PageSlotComponent entry={entry} routeId={routeId} />

  if (routeDef?.shell === 'bare') {
    return (
      <>
        <RouteProgressBarComponent />
        <TopRightStackComponent />
        <DialogComponent />
        <BannerStackComponent />
        {DebugOperationsComponent && (
          <React.Suspense fallback={null}>
            <DebugOperationsComponent />
          </React.Suspense>
        )}
        <div className="container">{page}</div>
      </>
    )
  }

  return (
    <>
      <RouteProgressBarComponent />
      <TopRightStackComponent />
      <DialogComponent />
      <BannerStackComponent />
      {DebugOperationsComponent && (
        <React.Suspense fallback={null}>
          <DebugOperationsComponent />
        </React.Suspense>
      )}
      <ShellComponent page={page} />
    </>
  )
})
